"use client"

import { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { collection, getDocs } from "firebase/firestore"
import { db } from "../firebase"
import {
  FaUsers,
  FaSpinner,
  FaExclamationTriangle,
  FaStar,
  FaCrown,
  FaUserTie,
  FaHandshake,
  FaArrowLeft,
  FaGraduationCap,
  FaLinkedin,
  FaEnvelope,
  FaCalendarAlt,
} from "react-icons/fa"

const DynamicECDisplay = () => {
  const { year } = useParams()
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [visibleSections, setVisibleSections] = useState(new Set())
  const [selectedMember, setSelectedMember] = useState(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]))
          }
        })
      },
      { threshold: 0.1 },
    )
    document.querySelectorAll("[data-animate]").forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (year) {
      fetchECMembers(year)
    }
  }, [year])

  const fetchECMembers = async (academicYear) => {
    try {
      setLoading(true)
      setError(null)

      console.log("📥 Fetching EC members for:", academicYear)
      const path = `executiveCommittee/${academicYear}/members`
      console.log("📂 Firestore path:", path)

      const snapshot = await getDocs(collection(db, path))

      console.log("📦 Documents fetched:", snapshot.size)
      snapshot.docs.forEach((doc) => console.log("✅", doc.id, doc.data()))

      const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })).filter((member) => !member.isPlaceholder)

      const categoryOrder = { board: 0, associates: 1, heads: 2, "co-heads": 3 }

      const sortedData = data.sort((a, b) => {
        const aOrder = categoryOrder[a.category] ?? 4
        const bOrder = categoryOrder[b.category] ?? 4
        if (aOrder !== bOrder) return aOrder - bOrder
        if (a.order !== undefined && b.order !== undefined) return a.order - b.order
        return a.name.localeCompare(b.name)
      })

      console.log("✅ Sorted members:", sortedData)
      setMembers(sortedData)
    } catch (err) {
      console.error("❌ Error fetching EC members:", err)
      setError("Failed to load Executive Committee members. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const getCategoryTitle = (category) => {
    switch (category) {
      case "board":
        return "Executive Board"
      case "associates":
        return "Associates"
      case "heads":
        return "Department Heads"
      case "co-heads":
        return "Co-Heads"
      default:
        return "Members"
    }
  }

  const getCategoryIcon = (category) => {
    switch (category) {
      case "board":
        return FaCrown
      case "associates":
        return FaUserTie
      case "heads":
        return FaStar
      case "co-heads":
        return FaHandshake
      default:
        return FaUsers
    }
  }

  const getCategoryColor = (category) => {
    switch (category) {
      case "board":
        return "from-purple-500 via-pink-500 to-red-500"
      case "associates":
        return "from-blue-500 via-indigo-500 to-purple-500"
      case "heads":
        return "from-green-500 via-emerald-500 to-teal-500"
      case "co-heads":
        return "from-orange-500 via-amber-500 to-yellow-500"
      default:
        return "from-gray-500 via-slate-500 to-gray-600"
    }
  }

  const getCategoryDescription = (category) => {
    switch (category) {
      case "board":
        return "The executive leadership team guiding our chapter's vision and strategy"
      case "associates":
        return "Dedicated members supporting various chapter initiatives and activities"
      case "heads":
        return "Department leaders overseeing specific areas of our chapter operations"
      case "co-heads":
        return "Assistant leaders working alongside department heads"
      default:
        return "Active members contributing to our chapter's success"
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-purple-900">
        <div className="text-center">
          <div className="relative">
            <FaSpinner className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-6" />
            <div className="absolute inset-0 bg-blue-600/20 rounded-full animate-ping"></div>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">Loading Committee</h2>
          <p className="text-lg text-gray-600 dark:text-gray-400">Fetching Executive Committee members...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 via-white to-pink-50 dark:from-red-900/10 dark:via-gray-800 dark:to-pink-900/10">
        <div className="text-center max-w-md bg-white dark:bg-gray-800 p-8 rounded-3xl shadow-2xl">
          <FaExclamationTriangle className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">Oops! Something went wrong</h2>
          <p className="text-red-600 dark:text-red-400 mb-6">{error}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => fetchECMembers(year)}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-full font-semibold transition-all duration-300 hover:scale-105 shadow-lg"
            >
              Try Again
            </button>
            <Link
              to="/ec"
              className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-full font-semibold transition-all duration-300 hover:scale-105 shadow-lg inline-flex items-center gap-2"
            >
              <FaArrowLeft className="w-4 h-4" />
              Back to Years
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-purple-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-teal-600 text-white py-16">
        <div className="absolute inset-0 bg-black/10"></div>

        {/* Floating Background Elements */}
        <div className="absolute top-10 left-10 w-20 h-20 bg-white/10 rounded-full animate-float"></div>
        <div className="absolute top-32 right-20 w-16 h-16 bg-white/10 rounded-full animate-float-delayed"></div>
        <div className="absolute bottom-10 left-1/4 w-12 h-12 bg-white/10 rounded-full animate-float"></div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/ec"
            className="inline-flex items-center gap-2 text-blue-100 hover:text-white transition-colors duration-200 mb-6 group"
          >
            <FaArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-200" />
            <span>Back to Year Selection</span>
          </Link>

          <div className="text-center">
            <div className="mb-6">
              <FaGraduationCap className="w-16 h-16 mx-auto mb-4 text-white/90" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-extrabold mb-4 bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent">
              Executive Committee
            </h1>
            <div className="flex items-center justify-center gap-3 mb-6">
              <FaCalendarAlt className="w-6 h-6 text-blue-200" />
              <span className="text-2xl sm:text-3xl font-bold text-blue-100">{year}</span>
            </div>
            <p className="text-lg text-blue-100 max-w-2xl mx-auto">
              Meet the dedicated leaders who shaped our chapter during the {year} academic year
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {members.length === 0 ? (
          <div className="text-center py-20">
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-12 max-w-md mx-auto">
              <FaUsers className="text-gray-400 w-16 h-16 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-gray-700 dark:text-white mb-4">No Members Found</h2>
              <p className="text-gray-500 dark:text-gray-400 mb-6">
                This academic year doesn't have any committee members yet.
              </p>
              <Link
                to="/ec"
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-3 rounded-full font-semibold transition-all duration-300 hover:scale-105 shadow-lg inline-flex items-center gap-2"
              >
                <FaArrowLeft className="w-4 h-4" />
                Back to Years
              </Link>
            </div>
          </div>
        ) : (
          <>
            {/* Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16">
              {["board", "associates", "heads", "co-heads"].map((category) => {
                const count = members.filter((m) => m.category === category).length
                const Icon = getCategoryIcon(category)
                const gradient = getCategoryColor(category)

                return (
                  <div
                    key={category}
                    className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400 capitalize">
                          {category === "co-heads" ? "Co-Heads" : category}
                        </p>
                        <p className="text-3xl font-bold text-gray-900 dark:text-white">{count}</p>
                      </div>
                      <div className={`p-3 rounded-full bg-gradient-to-r ${gradient}`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Members by Category */}
            {["board", "associates", "heads", "co-heads"].map((category) => {
              const categoryMembers = members.filter((m) => m.category === category)
              if (categoryMembers.length === 0) return null

              const Icon = getCategoryIcon(category)
              const gradient = getCategoryColor(category)

              return (
                <section key={category} className="mb-20" data-animate id={`section-${category}`}>
                  <div className="text-center mb-12">
                    <div
                      className={`inline-flex p-6 rounded-full bg-gradient-to-r ${gradient} text-white shadow-2xl mb-6 hover:scale-110 transition-transform duration-300`}
                    >
                      <Icon className="w-8 h-8" />
                    </div>
                    <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 dark:text-white mb-4">
                      {getCategoryTitle(category)}
                    </h2>
                    <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                      {getCategoryDescription(category)}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                    {categoryMembers.map((member, index) => (
                      <div
                        key={member.id}
                        className="group bg-white dark:bg-gray-800 rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-500 hover:scale-105 cursor-pointer"
                        style={{ animationDelay: `${index * 0.1}s` }}
                        onClick={() => setSelectedMember(member)}
                      >
                        <div className="relative overflow-hidden">
                          <img
                            src={member.photoUrl || "/placeholder.svg?height=300&width=300"}
                            alt={member.name}
                            onError={(e) => (e.target.src = "/placeholder.svg?height=300&width=300")}
                            className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          <div
                            className={`absolute top-4 right-4 p-2 rounded-full bg-gradient-to-r ${gradient} text-white shadow-lg`}
                          >
                            <Icon className="w-4 h-4" />
                          </div>
                        </div>

                        <div className="p-6 text-center">
                          <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                            {member.name}
                          </h3>
                          <p className="text-blue-600 dark:text-blue-400 font-semibold mb-3">{member.position}</p>
                          {member.department && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{member.department}</p>
                          )}
                          <div className="flex justify-center gap-2">
                            {member.email && (
                              <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-full hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors duration-200">
                                <FaEnvelope className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                              </div>
                            )}
                            {member.linkedin && (
                              <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-full hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors duration-200">
                                <FaLinkedin className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )
            })}

            {/* Call to Action */}
           
          </>
        )}
      </div>

      {/* Member Detail Modal */}
      {selectedMember && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedMember(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <img
                src={selectedMember.photoUrl || "/placeholder.svg?height=200&width=200"}
                alt={selectedMember.name}
                className="w-32 h-32 rounded-full mx-auto mb-4 object-cover"
              />
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{selectedMember.name}</h3>
              <p className="text-blue-600 dark:text-blue-400 font-semibold mb-4">{selectedMember.position}</p>
              {selectedMember.department && (
                <p className="text-gray-600 dark:text-gray-300 mb-4">{selectedMember.department}</p>
              )}
              <div className="flex justify-center gap-4 mb-6">
                {selectedMember.email && (
                  <a
                    href={`mailto:${selectedMember.email}`}
                    className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors duration-200"
                  >
                    <FaEnvelope className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </a>
                )}
                {selectedMember.linkedin && (
                  <a
                    href={selectedMember.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors duration-200"
                  >
                    <FaLinkedin className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </a>
                )}
              </div>
              <button
                onClick={() => setSelectedMember(null)}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-full font-semibold transition-colors duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 8s ease-in-out infinite;
        }
      `}</style>
    </div>
  )
}

export default DynamicECDisplay
